import { useState, useEffect } from 'react';
import { customAlphabet } from 'nanoid';
import TagManager from 'react-gtm-module';

import { IAddress, ISelection, IDevice } from '../types';
import { getDeviceInfo } from './device-info';
import { isActive } from './feature-flag';
import { getLogger } from './logger-plus';
import { v4 as uuid } from 'uuid';
import userHasRole from './user-has-role';
import { IDeviceInfo } from '../types';

const logger = getLogger('lib');
interface Selection {
  currentPriceNum: string;
  currentPriceDen: string;
}

export const orderByPrice = (a: Selection, b: Selection) =>
  Number.parseInt(formatPrice(a.currentPriceNum, a.currentPriceDen)) -
  Number.parseInt(formatPrice(b.currentPriceNum, b.currentPriceDen));

export const filterDisplayed = (selections: ISelection[]) =>
  selections.filter(({ displayed }: ISelection) => displayed === 'true');

export const defaultDevice: IDevice = {
  acceptedTender: '£ $',
  accountId: '',
  balances: [],
  currency: 'USD',
  deviceId: '',
  label: 'Window',
  qtyToday: 9,
  qtyTodayLabel: 'Bets Today',
  slotNumber: 1,
  deviceAttributes: null,
  type: 'POS',
  isMyDevice: false
};

export type CorrelationIdHeaders = ReturnType<typeof generateCorrelationIdHeaders>;

export function generateCorrelationIdHeaders() {
  return { 'Correlation-Id': generateCorrelationId() };
}

export const generateCorrelationId = () => customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 12)();

export const generateActivityId = () => uuid();

export const handleAddSelection =
  (onSelectHandler: any, selection: ISelection, isSelected: boolean = false) =>
  () =>
    onSelectHandler(selection, isSelected);

export const formatSecondsForCountdown = (count: number) =>
  `${Math.floor(count / 60)}:${('00' + (count % 60)).slice(-2)}`;

export const useCountdown = (fromSeconds: number, onComplete: Function | undefined) => {
  const [count, setCount] = useState(fromSeconds);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (count === 0) {
        if (onComplete) {
          onComplete();
        }
        return;
      }
      setCount(count - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [count, onComplete]);

  return count;
};

export const sortByLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);

export const sortByMultipleSorters = (...sorters: any[]) => {
  return (first: any, second: any) => {
    let sortResult = 0;
    for (let i = 0; i < sorters.length; i++) {
      sortResult = sorters[i](first, second);
      if (sortResult !== 0) {
        break;
      }
    }
    return sortResult;
  };
};

// eslint-disable-next-line prefer-spread
export const flatter = <A>(arr: A[]): A => [].concat.apply([], arr);

export const formatBettingInterestNumber = (original: number) => {
  if (original.toString().length === 1) {
    return parseInt('30' + original, 10);
  }
  if (original.toString().length === 2) {
    return parseInt('3' + original, 10);
  }
  return original;
};

export const isPriceValid = (numerator: string, denominator: string): boolean =>
  !isNaN(fractionalToDecimal(numerator, denominator));

export const fractionalToDecimal = (numerator: string, denominator: string) =>
  parseInt(numerator, 10) / parseInt(denominator, 10);

const roundToTwo = (value: number) => Math.round(value * 100) / 100;

const fractionalOddsToDecimalExceptions = {
  '13/8': 2.62,
  '15/8': 2.87,
  '11/8': 2.37,
  '8/13': 1.61,
  '2/7': 1.28,
  '1/8': 1.12,
  '123/200': 1.62
};

const getFractionalOddsToDecimalException = (numerator: string, denominator: string): number | undefined =>
  fractionalOddsToDecimalExceptions[`${numerator}/${denominator}`];

export const convertFractionalOddsToDecimal = (numerator: string, denominator: string): number =>
  getFractionalOddsToDecimalException(numerator, denominator) ||
  roundToTwo(fractionalToDecimal(numerator, denominator) + 1);

export const convertMoneylineOdds = (decimalOdds: number): string => {
  if (decimalOdds >= 1) {
    return '+' + Math.round(decimalOdds * 100);
  } else {
    return Math.round(-100 / decimalOdds).toString();
  }
};

export const formatDecimalPrice = (decimalPrice: number): string => convertMoneylineOdds(decimalPrice - 1);

export const formatPrice = (num: string, den: string, decimal?: number): string => {
  const decimalOdds = decimal !== undefined ? decimal - 1 : fractionalToDecimal(num, den);
  return convertMoneylineOdds(decimalOdds);
};

export const formatPriceFactory = (num: string, den: string, decimal?: number): string => {
  const odds: any = formatPrice(num, den, decimal);

  if (isNaN(odds)) {
    logger.error(`Odds are not valid ${[num, den, decimal].join(' ')}`);
    return '--';
  }

  return odds;
};

export const getEvent = (props: any) => props.event || props.events[0] || {};

export const removePipes = (name: string) => name.replace(/\|/g, '');

export const formatNumber = (num: number) => (num > 0 ? `+${num}` : `${num}`);

export const calculateHandicap = (hcap: number | null, fbResult?: string) => {
  if (hcap !== 0 && hcap !== null && fbResult?.toLowerCase().startsWith('a')) {
    return hcap * -1;
  }
  return hcap;
};

export const isYesNoMarket = (name?: string) => {
  const type = name?.toLowerCase();
  return type === 'yes' || type === 'no';
};

export const isTotalsMarket = (name?: string) => {
  const type = name?.toLowerCase();
  return type === 'over' || type === 'under';
};

export const formatHandicap = (
  hcap: number | null,
  name: string,
  prependName: boolean = true,
  abbreviateName: boolean = false
): string => {
  if (hcap === null || hcap === undefined) {
    return '';
  }

  if (hcap === 0) {
    return 'PICK';
  }

  if (isTotalsMarket(name)) {
    return prependName ? `${abbreviateName ? name[0] : name} ${hcap}` : `${hcap}`;
  }

  return formatNumber(hcap);
};

export const withRootClassName = (rootClassName: string) => (suffix?: string) =>
  suffix ? `${rootClassName}-${suffix}` : rootClassName;

export const withRootTestId = (rootTestId: string) => withRootClassName(rootTestId);

export const isUserAccount = (accountType: string) => ['WRITER', 'CAGE_USER'].includes(accountType);

export interface ICategorised {
  type: string;
  category?: string | null;
}

export const isTypeMatch = (candidate: ICategorised, type: string, category: string | null = null) => {
  if (category || candidate.category) {
    return candidate.type === type && category === candidate.category;
  }
  return candidate.type === type;
};

export const canDevicePayout = (deviceInfo = getDeviceInfo()): boolean => isActive(deviceInfo?.features?.canPayout);

export const canUserCashOut = (deviceInfo = getDeviceInfo()): boolean => {
  if ('KIOSK' === deviceInfo?.slotId?.type) {
    return isActive(deviceInfo?.features?.cashOut);
  }
  const userToken = localStorage.getItem('user');
  const requiredRoles = deviceInfo?.features?.cashOut?.roles || [];
  return isActive(deviceInfo?.features?.cashOut) && userToken !== null && userHasRole(requiredRoles, userToken);
};

export const canPlaceBet = (playerCardId: string | undefined, deviceInfo = getDeviceInfo()): boolean =>
  Boolean(!isActive(deviceInfo?.features?.preventAnonymousBets) || playerCardId);

export const getPrintableAddress = (address: IAddress) => ({
  ...address,
  zipCode: address.postCode,
  addressLine1: address.line1
});

export function correctFbResult<Team extends { name: string }>(
  teams: Team[],
  markets: { selections: { fbResult: string }[] }[]
) {
  const homogenizeString = (str: string | undefined) => str?.toLowerCase().trim();
  const matchSelection = teamName => selection => homogenizeString(teamName) === homogenizeString(selection.name);

  const calculateFbResult = (fbResult: string | undefined, otherFbResult: string | undefined) =>
    fbResult || (otherFbResult?.toLowerCase().charAt(0) === 'h' ? 'away' : 'home');

  const selections = flatter(markets.map(market => market.selections)).filter(
    (selection: ISelection) => selection.fbResult?.toLowerCase() !== 'generic'
  );

  const [team1, team2] = teams;
  if (!team1 || !team2) {
    return teams;
  }

  const team1Selection = selections.find(matchSelection(team1?.name));
  const team2Selection = selections.find(matchSelection(team2?.name));

  return team1Selection || team2Selection
    ? [
        { name: team1.name, fbResult: calculateFbResult(team1Selection?.fbResult, team2Selection?.fbResult) },
        { name: team2.name, fbResult: calculateFbResult(team2Selection?.fbResult, team1Selection?.fbResult) }
      ]
    : teams;
}

/**
 * Do not use default of 'features' parameter. Pass an argument instead e.g. from useDeviceInfo.
 * The default calls getDeviceInfo every time (e.g. on every render) which unnecessarily reduces performance.
 */
export const getBuyingPointsFlags = (
  sportId: string | undefined,
  features: IDeviceInfo['features'] = getDeviceInfo().features
) => {
  if (!sportId) {
    return {
      buyingPointsFixed: false,
      buyingPointsPYS: false,
      playerAltLines: false,
      movingLines: false
    };
  }

  const buyingPointsPYS =
    features?.buyingPointsPYS?.enabled && (features?.buyingPointsPYS?.sports ?? []).includes(sportId);

  const buyingPointsFixed =
    !buyingPointsPYS &&
    features?.buyingPointsFixed?.enabled &&
    (features?.buyingPointsFixed?.sports ?? []).includes(sportId);

  const playerAltLines =
    features?.playerAltLines?.enabled && (features?.playerAltLines?.sports ?? []).includes(sportId);

  const movingLines = features?.movingLines?.enabled && (features?.movingLines?.sports ?? []).includes(sportId);

  return {
    buyingPointsFixed: Boolean(buyingPointsFixed),
    buyingPointsPYS: Boolean(buyingPointsPYS),
    playerAltLines: Boolean(playerAltLines),
    movingLines: Boolean(movingLines)
  };
};

export function formatTicketOrVoucherId(id: string, maskId: boolean) {
  const formattedId = id.replace(/(\d{6})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4');
  return maskId ? `XXXXXX-XXXX-XXXX-${formattedId.slice(-4)}` : formattedId;
}

export const fireGtmEvent = <T>(event: string, additionalDataLayerProps: Record<string, T> = {}) => {
  if (window['USROSI'] !== undefined && TagManager?.dataLayer) {
    try {
      const dataLayer = { event, ...additionalDataLayerProps };
      TagManager.dataLayer({
        dataLayer,
        dataLayerName: 'USROSI'
      });
      logger.info(dataLayer, 'GTM data layer updated');
    } catch (error) {
      logger.error(error, 'Failed to update GTM data layer');
    }
  }
};

export const hasSgpBet = (bets: { type: string }[]) => bets?.some(bet => bet.type === 'BYO');

export function isNotUndefined<Value>(valueOrUndefined: Value | undefined): valueOrUndefined is Value {
  return valueOrUndefined !== undefined;
}

export function isNotNull<Value>(valueOrNull: Value | null): valueOrNull is Value {
  return valueOrNull !== null;
}
